import {
    commaSeperatedAisleRangeStringToArray,
    commaSeperatedLocationRangeStringToArray,
    commaSeperatedStringToArray,
    getNodeId
} from "../../../Utility";
import {
    CYCLE,
    INCLUDED_LOCATION_OPTIONS, PS_ON_GRID,
    RUSH_1_HOUR,
    RUSH_1_HOUR_NAME,
    RUSH_3_HOUR,
    RUSH_3_HOUR_NAME,
    SORT_PATH_FILL_ALGORITHMS,
    SORT_PATH_PICK_ALGORITHMS, SORT_PATH_SETTING_STATUS,
    StationCycleState
} from "../../../Constants";
import {addToObject, chainWalk} from "../CommonUtils";
import {getGrocerySortPathSettingName, isDynamicCycle} from "./CommonUtils";
import {FeatureConfigUtils} from "../FeatureConfigUtils";

export const buildSortPathConfigurationDataObject = (formData, clusterResources = []) => {
    const { commonFormData, fillFormData, pickFormData } = formData;

    /**
     * This is executed in case of SSD where cycle name is decided in UI based on input parameters chosen
     * Based on input parameters, if it is PSOnGrid sort path then cycle name, cycle value and additional params are set here
     */
    if (commonFormData && commonFormData.isPSOnGridSortPath) {
        commonFormData.name = PS_ON_GRID;
        commonFormData.cycle = PS_ON_GRID;
        commonFormData.additionalParams = {
            workflowType: PS_ON_GRID
        }
    }

    if (commonFormData && commonFormData.isGrocerySortPath && commonFormData.additionalParams != null) {
        commonFormData.name = getGrocerySortPathSettingName(commonFormData.additionalParams.temperature, commonFormData.volumeType);
    }

    if (commonFormData && commonFormData.isRushSortPath) {
        commonFormData.name = RUSH_1_HOUR_NAME;
        commonFormData.additionalParams = {
            workflowType: RUSH_1_HOUR
        }
    }

    if (commonFormData && commonFormData.isRush3hSortPath) {
        commonFormData.name = RUSH_3_HOUR_NAME;
        commonFormData.additionalParams = {
            workflowType: RUSH_3_HOUR
        }
    }

    // Do not populate additionalParams if not a grocery or a PS On-Grid sort path and not a rush sort path
    if (commonFormData && !(commonFormData.isGrocerySortPath || commonFormData.isPSOnGridSortPath || commonFormData.isRushSortPath || commonFormData.isRush3hSortPath)) {
        commonFormData.additionalParams = null;
    }

    /**
     * This is executed in non-SSD sites where cycle is chosen in UI from a list of eligible cycles received from backend
     * Since cycle is already decided, only additional params are set here
     */
    if (commonFormData && commonFormData.cycle && commonFormData.cycle === CYCLE.PS_ON_GRID) {
        commonFormData.additionalParams = {
            workflowType: PS_ON_GRID
        }
    }

    return {
        settingId: chainWalk(() => commonFormData.gridAllocationId,  null),
        name: chainWalk(() => commonFormData.name,  "").trim(),
        cycle: chainWalk(() => commonFormData.cycle,  null),
        nodeId: chainWalk(() => commonFormData.nodeId,  null) ,
        binType: chainWalk(() => commonFormData.binType,  null),
        volumeType: chainWalk(() => commonFormData.volumeType, null),
        cycleList: chainWalk(() => commonFormData.cycleList,  null),
        injectionType: chainWalk(() => commonFormData.injectionType,  null),
        inductLocationType:  chainWalk(() => commonFormData.inductLocationType, null),
        status: chainWalk(() => commonFormData.status, SORT_PATH_SETTING_STATUS.UPDATED),
        additionalParams: chainWalk(() => commonFormData.additionalParams, null),
        excludedLocations: {
            excludedClusters: commaSeperatedStringToArray(chainWalk(() => commonFormData.excludedLocations.excludedClusters, null)),
            excludedAisles: commaSeperatedStringToArray(chainWalk(() => commonFormData.excludedLocations.excludedAisles, null)),
            excludedBins: commaSeperatedStringToArray(chainWalk(() => commonFormData.excludedLocations.excludedBins, null)),
            excludedFloorLocations: commaSeperatedStringToArray(chainWalk(() => commonFormData.excludedLocations.excludedFloorLocations, null)),
        },
        includedLocations: {
            includedAisles: commaSeperatedAisleRangeStringToArray(chainWalk(() => commonFormData.includedLocations.aislesRange[1], null)),
            includedColumns: commaSeperatedStringToArray(chainWalk(() => commonFormData.includedLocations.columns[1], null)),
            includedSortZones: commaSeperatedStringToArray(chainWalk(() => commonFormData.includedLocations.includedSortZones, null)),
            includedSortLocations: commaSeperatedLocationRangeStringToArray(chainWalk(() => commonFormData.includedLocations.sortLocationsRange[1], null)),
        },
        fillPathSetting: buildFillPathSetting(fillFormData, clusterResources),
        pickPathSetting: buildPickPathSetting(pickFormData)
    };
}

const buildFillPathSetting = (fillFormData, clusterResources) => {
    return {
        algorithm: chainWalk(() => fillFormData.algorithm, null),
        additionalFields: buildFillPathAdditionalFields(fillFormData, clusterResources),
    };
}

const buildFillPathAdditionalFields = (fillFormData, clusterResources) => {
    const algorithm = chainWalk(() => fillFormData.algorithm, null);
    switch (algorithm) {
        case SORT_PATH_FILL_ALGORITHMS.COMBINATRIX:
            return {
                fingerOrder: {
                    type: chainWalk(() => fillFormData.resourceSequences.fingerSequence[0], null),
                    customSequence: commaSeperatedStringToArray(chainWalk(() => fillFormData.resourceSequences.fingerSequence[1], null)),
                },
                aisleOrder: {
                    type: chainWalk(() => fillFormData.resourceSequences.aisleSequence[0], null),
                    customSequence: commaSeperatedStringToArray(chainWalk(() => fillFormData.resourceSequences.aisleSequence[1], null)),
                },
                adtaPrioritizationOrder: chainWalk(() => fillFormData.resourceSequences.adtaPrioritySequence[0], null),
                deprioritizedTiers: chainWalk(() => fillFormData.commonParameters.deprioritizedTiers, []),
                deprioritizedTiersForADTAClusters: chainWalk(() => fillFormData.commonParameters.deprioritizedTiersForADTAClusters, []),
                scienceModelOptimizationParameter: {
                    balancingSortZonesInClusters: chainWalk(() => fillFormData.commonParameters.balancingSortZonesInClusters, false),
                    optimiseClusterUsage: chainWalk(() => fillFormData.commonParameters.optimiseClusterUsage, true),
                },
                maxClusterCapacityForNode: buildCusterParameterModelForSortPathSettings(chainWalk(() => fillFormData.commonParameters.maxClusterCapacityForNode, [])),
                adtaClusterIdentifierForNode: buildCusterParameterModelForSortPathSettings(chainWalk(() =>fillFormData.commonParameters.adtaClusterIdentifierForNode, [])),
                blockLength: buildCusterParameterModelForSortPathSettings(chainWalk(() => fillFormData.commonParameters.blockLength, [])),
                splitPercentage: chainWalk(() => fillFormData.parameters.splitPercentage, null),
            };
        case SORT_PATH_FILL_ALGORITHMS.STANDARD:
        case SORT_PATH_FILL_ALGORITHMS.STANDARD_BLOCK:
            return {
                clusterOrder: {
                    type: chainWalk(() => fillFormData.resourceSequences.clusterSequence[0], null),
                    customSequence: commaSeperatedStringToArray(chainWalk(() => fillFormData.resourceSequences.clusterSequence[1], null)),
                },
                aisleOrder: {
                    type: chainWalk(() => fillFormData.resourceSequences.aisleSequence[0], null),
                    customSequence: commaSeperatedStringToArray(chainWalk(() => fillFormData.resourceSequences.aisleSequence[1], null)),
                },
                adtaPrioritizationOrder: chainWalk(() => fillFormData.resourceSequences.adtaPrioritySequence[0], null),
                deprioritizedTiers: chainWalk(() => fillFormData.commonParameters.deprioritizedTiers, []),
                deprioritizedTiersForADTAClusters: chainWalk(() => fillFormData.commonParameters.deprioritizedTiersForADTAClusters, []),
                scienceModelOptimizationParameter: {
                    balancingSortZonesInClusters: chainWalk(() => fillFormData.commonParameters.balancingSortZonesInClusters, false),
                    optimiseClusterUsage: chainWalk(() => fillFormData.commonParameters.optimiseClusterUsage, true),
                },
                maxClusterCapacityForNode: buildCusterParameterModelForSortPathSettings(chainWalk(() => fillFormData.commonParameters.maxClusterCapacityForNode, [])),
                adtaClusterIdentifierForNode: buildCusterParameterModelForSortPathSettings(chainWalk(() =>fillFormData.commonParameters.adtaClusterIdentifierForNode, [])),
                blockLength: buildCusterParameterModelForSortPathSettings(chainWalk(() => fillFormData.commonParameters.blockLength, [])),
            };
        case SORT_PATH_FILL_ALGORITHMS.FLOOR_LOCATION_STANDARD_FILL_PATH:
            return {
                clusterOrder: {
                    type: chainWalk(() => fillFormData.resourceSequences.clusterSequence[0], null),
                    customSequence: commaSeperatedStringToArray(chainWalk(() => fillFormData.resourceSequences.aisleSequence[1], null)),
                    clusterSequenceForNode: buildCusterParameterModelForSortPathSettings(chainWalk(() => fillFormData.commonParameters.clusterSequenceForNode, [])),
                },
                aisleOrder: {
                    type: chainWalk(() => fillFormData.resourceSequences.aisleSequence[0], null),
                    customSequence: commaSeperatedStringToArray(chainWalk(() => fillFormData.resourceSequences.aisleSequence[1], null)),
                },
                adtaPrioritizationOrder: chainWalk(() => fillFormData.resourceSequences.adtaPrioritySequence[0], null),
                deprioritizedTiers: chainWalk(() => fillFormData.commonParameters.deprioritizedTiers, []),
                deprioritizedTiersForADTAClusters: chainWalk(() => fillFormData.commonParameters.deprioritizedTiersForADTAClusters, []),
                scienceModelOptimizationParameter: {
                    balancingSortZonesInClusters: chainWalk(() => fillFormData.commonParameters.balancingSortZonesInClusters, false),
                    optimiseClusterUsage: chainWalk(() => fillFormData.commonParameters.optimiseClusterUsage, true),
                },
                maxClusterCapacityForNode: isSTAGEnabled() ?
                    buildClusterParameterModelForSortPathSettingswithDefaultValue(chainWalk(() => fillFormData.commonParameters.maxClusterCapacityForNode, []), clusterResources) :
                    buildCusterParameterModelForSortPathSettings(chainWalk(() => fillFormData.commonParameters.maxClusterCapacityForNode, [])),
                adtaClusterIdentifierForNode: buildCusterParameterModelForSortPathSettings(chainWalk(() =>fillFormData.commonParameters.adtaClusterIdentifierForNode, [])),
                blockLength: buildCusterParameterModelForSortPathSettings(chainWalk(() => fillFormData.commonParameters.blockLength, [])),
            };
        case SORT_PATH_FILL_ALGORITHMS.CLUSTER_WISE_STANDARD:
            return {
                clusterOrder: {
                    type: chainWalk(() => fillFormData.resourceSequences.clusterSequence[0], null),
                    customSequence: commaSeperatedStringToArray(chainWalk(() => fillFormData.resourceSequences.clusterSequence[1], null)),
                },
                aisleOrder: {
                    type: chainWalk(() => fillFormData.resourceSequences.aisleSequence[0], null),
                    customSequence: commaSeperatedStringToArray(chainWalk(() => fillFormData.resourceSequences.aisleSequence[1], null)),
                },
                adtaPrioritizationOrder: chainWalk(() => fillFormData.resourceSequences.adtaPrioritySequence[0], null),
                deprioritizedTiers: chainWalk(() => fillFormData.commonParameters.deprioritizedTiers, []),
                deprioritizedTiersForADTAClusters: chainWalk(() => fillFormData.commonParameters.deprioritizedTiersForADTAClusters, []),
                scienceModelOptimizationParameter: {
                    balancingSortZonesInClusters: chainWalk(() => fillFormData.commonParameters.balancingSortZonesInClusters, false),
                    optimiseClusterUsage: chainWalk(() => fillFormData.commonParameters.optimiseClusterUsage, true),
                },
                maxClusterCapacityForNode: buildCusterParameterModelForSortPathSettings(chainWalk(() => fillFormData.commonParameters.maxClusterCapacityForNode, [])),
                adtaClusterIdentifierForNode: buildCusterParameterModelForSortPathSettings(chainWalk(() =>fillFormData.commonParameters.adtaClusterIdentifierForNode, [])),
                blockLength: buildCusterParameterModelForSortPathSettings(chainWalk(() => fillFormData.commonParameters.blockLength, [])),
            };
        case SORT_PATH_FILL_ALGORITHMS.SAME_DAY_STANDARD_FILL:
            return {
                sortLocationOrder: {
                    type: chainWalk(() => fillFormData.resourceSequences.sortLocationSequence[0], null),
                    customSequence: commaSeperatedStringToArray(chainWalk(() => fillFormData.resourceSequences.sortLocationSequence[1], null)),
                } };
        default:
            return {};
    };
}

const buildPickPathSetting = (pickFormData) => {
    return {
        algorithm: chainWalk(() => pickFormData.algorithm, null),
        additionalFields: (FeatureConfigUtils.getIsSSD3EnabledStation()
            || FeatureConfigUtils.getIsSSDGrocerySortPathEnabledStation()
            || FeatureConfigUtils.getIsRushSortPathEnabledStation()) ? buildPickPathAdditionalFieldsSSD3(pickFormData) :
            buildPickPathAdditionalFields(pickFormData),
    };
}

const buildPickPathAdditionalFields = (pickFormData) => {
    return {
        clusterOrder: {
            type: chainWalk(() => pickFormData.resourceSequences.clusterSequence[0], null),
            customSequence: commaSeperatedStringToArray(chainWalk(() => pickFormData.resourceSequences.clusterSequence[1], null)),
        },
        aisleOrder: {
            type: chainWalk(() => pickFormData.resourceSequences.aisleSequence[0], null),
            customSequence: commaSeperatedStringToArray(chainWalk(() => pickFormData.resourceSequences.aisleSequence[1], null)),
        },
        columnLengthForZebraPath: chainWalk(() => pickFormData.parameters.columnLengthForZebraPath, null),
        columnSplitRatio: "0.5",
    };
};

const buildPickPathAdditionalFieldsSSD3 = (pickFormData) => {
    return {
        clusterOrder: {
            type: chainWalk(() => pickFormData.resourceSequences.clusterSequence[0], null),
            customSequence: commaSeperatedStringToArray(chainWalk(() => pickFormData.resourceSequences.clusterSequence[1], null)),
        },
        sortLocationOrder: {
            type: chainWalk(() => pickFormData.resourceSequences.sortLocationSequence[0], null),
            customSequence: commaSeperatedStringToArray(chainWalk(() => pickFormData.resourceSequences.sortLocationSequence[1], null)),
        }
    };
};

export const getGridAllocationFormData = (sortPathConfiguration, cycleList) => {
    const fillAlgorithm = FeatureConfigUtils.getIsStagFeatureEnabledStation() ?
        SORT_PATH_FILL_ALGORITHMS.FLOOR_LOCATION_STANDARD_FILL_PATH :
        isDynamicCycle(sortPathConfiguration.cycle, sortPathConfiguration.nodeId, cycleList) ?
        SORT_PATH_FILL_ALGORITHMS.CLUSTER_WISE_STANDARD: (FeatureConfigUtils.getIsSSD3EnabledStation()
            || FeatureConfigUtils.getIsSSDGrocerySortPathEnabledStation()) ?
            SORT_PATH_FILL_ALGORITHMS.SAME_DAY_STANDARD_FILL: chainWalk(() => sortPathConfiguration.fillPathSetting.algorithm,
                null);
    const nodeId = chainWalk(() => sortPathConfiguration.nodeId, getNodeId());
    const cycleName = chainWalk(() => sortPathConfiguration.cycle, null);
    const pickAlgorithm = (FeatureConfigUtils.getIsSSD3EnabledStation()
        || FeatureConfigUtils.getIsSSDGrocerySortPathEnabledStation()) ? SORT_PATH_PICK_ALGORITHMS.SAME_DAY_STANDARD_PATH :
        chainWalk(() => sortPathConfiguration.pickPathSetting.algorithm, null);

    let isGrocerySortPath = false;
    let isRushSortPath = false;
    let isRush3hSortPath = false;
    let isPSOnGridSortPath = false;

    const isSSDStation = FeatureConfigUtils.isSSDStation();

    if (sortPathConfiguration.additionalParams) {
        if (sortPathConfiguration.additionalParams.temperature) {
            isGrocerySortPath = true;
        }

        else if(sortPathConfiguration.additionalParams.workflowType) {
            switch (sortPathConfiguration.additionalParams.workflowType) {
                case RUSH_1_HOUR:
                    isRushSortPath = true;
                    break;
                case RUSH_3_HOUR:
                    isRush3hSortPath = true;
                    break;
                case PS_ON_GRID:
                    // Only SSD stations model PS On-Grid as a sort path type; non-SSD sites use a PS_ON_GRID cycle
                    // with a user-chosen name, which must not be overwritten on re-submit.
                    isPSOnGridSortPath = isSSDStation;
                    break;
                default: 
                    break;
            }
        }
    }

    return {
        commonFormData: {
            gridAllocationId: chainWalk(() => sortPathConfiguration.settingId, null),
            cycle: cycleName,
            nodeId: nodeId,
            status: chainWalk(() => sortPathConfiguration.status, SORT_PATH_SETTING_STATUS.UPDATED),
            cycleList: chainWalk(() => sortPathConfiguration.cycleList, [{
                [StationCycleState.STATION_CODE] : nodeId,
                [StationCycleState.CYCLE] : {
                    [StationCycleState.CYCLE_NAME]: cycleName,
                    [StationCycleState.CYCLE_ID]: cycleName
                }
            }]),
            name: chainWalk(() => sortPathConfiguration.name, null),
            binType: chainWalk(() => sortPathConfiguration.binType, null),
            volumeType: chainWalk(() => sortPathConfiguration.volumeType, null),
            additionalParams: chainWalk(() => sortPathConfiguration.additionalParams, null),
            isGrocerySortPath: isGrocerySortPath,
            isRushSortPath: isRushSortPath,
            isRush3hSortPath: isRush3hSortPath,
            isPSOnGridSortPath: isPSOnGridSortPath,
            injectionType: chainWalk(() => sortPathConfiguration.injectionType,  null),
            inductLocationType:  chainWalk(() => sortPathConfiguration.inductLocationType, null),
            excludedLocations: {
                excludedClusters: chainWalk(() => sortPathConfiguration.excludedLocations.excludedClusters, []).join(", "),
                excludedAisles: chainWalk(() => sortPathConfiguration.excludedLocations.excludedAisles, []).join(", "),
                excludedBins: chainWalk(() => sortPathConfiguration.excludedLocations.excludedBins, []).join(", "),
                excludedFloorLocations: chainWalk(() => sortPathConfiguration.excludedLocations.excludedFloorLocations, []).join(", "),
            },
            includedLocations: {
                ...(chainWalk(() => sortPathConfiguration.includedLocations.includedAisles, []).length) ?
                {
                    aislesRange: [INCLUDED_LOCATION_OPTIONS.COMMON.CUSTOM,
                        chainWalk(() => sortPathConfiguration.includedLocations.includedAisles, []).map(x => `${x.startingAisle}:${x.endingAisle}`).join(", ")],
                } : { aislesRange: [INCLUDED_LOCATION_OPTIONS.AISLES.USE_ALL, null] },
                ...(chainWalk(() => sortPathConfiguration.includedLocations.includedColumns, []).length) ? {
                    columns: [INCLUDED_LOCATION_OPTIONS.COMMON.CUSTOM, chainWalk(() => sortPathConfiguration.includedLocations.includedColumns, []).join(", ")]
                } : { columns: [INCLUDED_LOCATION_OPTIONS.COLUMNS.ABC, null] },
                ...(chainWalk(() => sortPathConfiguration.includedLocations.includedSortZones, []).length) ? {
                    includedSortZones: chainWalk(() => sortPathConfiguration.includedLocations.includedSortZones, []).join(", ")
                } : { includedSortZones: null },
                ...isSSDStation ? (chainWalk(() => sortPathConfiguration.includedLocations.includedSortLocations, []).length) ? {
                    sortLocationsRange: [INCLUDED_LOCATION_OPTIONS.COMMON.CUSTOM,
                        chainWalk(() => sortPathConfiguration.includedLocations.includedSortLocations, [])
                            .map(x => `${x.startingLocation}:${x.endingLocation}`).join(", ")],
                } : { sortLocationsRange: [INCLUDED_LOCATION_OPTIONS.SORT_LOCATIONS.USE_ALL, null] } : {}
            },
            overlapList : chainWalk(() => sortPathConfiguration.overlapList, null),
        },
        fillFormData: {
            algorithm: fillAlgorithm,
            resourceSequences: {
                ...(fillAlgorithm === SORT_PATH_FILL_ALGORITHMS.COMBINATRIX) &&
                {
                    fingerSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.fingerOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.fingerOrder.customSequence, []).join(", ")
                    ],
                    aisleSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.customSequence, []).join(", ")
                    ],
                    adtaPrioritySequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.adtaPrioritizationOrder, null),
                    ]
                },
                ...(fillAlgorithm === SORT_PATH_FILL_ALGORITHMS.FLOOR_LOCATION_STANDARD_FILL_PATH) &&
                {
                    clusterSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.clusterOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.clusterOrder.customSequence, []).join(", "),
                    ],
                    aisleSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.customSequence, []).join(", ")
                    ]
                },
                ...(fillAlgorithm === SORT_PATH_FILL_ALGORITHMS.STANDARD) &&
                {
                    clusterSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.clusterOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.clusterOrder.customSequence, []).join(", ")
                    ],
                    aisleSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.customSequence, []).join(", ")
                    ],
                    adtaPrioritySequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.adtaPrioritizationOrder, null),
                    ]
                },
                ...(fillAlgorithm === SORT_PATH_FILL_ALGORITHMS.CLUSTER_WISE_STANDARD) &&
                {
                    clusterSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.clusterOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.clusterOrder.customSequence, []).join(", ")
                    ],
                    aisleSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.customSequence, []).join(", ")
                    ],
                    adtaPrioritySequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.adtaPrioritizationOrder, null),
                    ]
                },
                ...(fillAlgorithm === SORT_PATH_FILL_ALGORITHMS.STANDARD_BLOCK) &&
                {
                    clusterSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.clusterOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.clusterOrder.customSequence, []).join(", ")
                    ],
                    aisleSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.aisleOrder.customSequence, []).join(", ")
                    ],
                    adtaPrioritySequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.adtaPrioritizationOrder, null),
                    ]
                },
                ...(fillAlgorithm === SORT_PATH_FILL_ALGORITHMS.SAME_DAY_STANDARD_FILL) &&
                {
                    sortLocationSequence: [
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.sortLocationOrder.type, null),
                        chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.sortLocationOrder.customSequence, []).join(", ")
                    ]
                }
            },
            parameters: {
                ...(fillAlgorithm === SORT_PATH_FILL_ALGORITHMS.COMBINATRIX) &&
                {
                    splitPercentage: chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.splitPercentage, null)
                }
            },
                commonParameters: {
                    deprioritizedTiers: chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.deprioritizedTiers, []),
                    deprioritizedTiersForADTAClusters:  chainWalk(() => sortPathConfiguration.fillPathSetting.additionalFields.deprioritizedTiersForADTAClusters, []),
                    balancingSortZonesInClusters: getScienceModelOptimizationParameter(sortPathConfiguration.fillPathSetting.additionalFields.scienceModelOptimizationParameter, "balancingSortZonesInClusters"),
                    optimiseClusterUsage: getScienceModelOptimizationParameter(sortPathConfiguration.fillPathSetting.additionalFields.scienceModelOptimizationParameter,"optimiseClusterUsage"),
                    maxClusterCapacityForNode: extractCusterParameterMapFromSortPathSettings(sortPathConfiguration.fillPathSetting.additionalFields.maxClusterCapacityForNode),
                    adtaClusterIdentifierForNode: extractCusterParameterMapFromSortPathSettings(sortPathConfiguration.fillPathSetting.additionalFields.adtaClusterIdentifierForNode),
                    blockLength: extractCusterParameterMapFromSortPathSettings(sortPathConfiguration.fillPathSetting.additionalFields.blockLength),
                    clusterSequenceForNode: extractCusterParameterMapFromSortPathSettings(chainWalk ( ()=> sortPathConfiguration.fillPathSetting.additionalFields.clusterOrder.clusterSequenceForNode, [])),
                },
        },
        pickFormData: {
            algorithm: pickAlgorithm,
            resourceSequences: {
                clusterSequence: [
                    chainWalk(() => sortPathConfiguration.pickPathSetting.additionalFields.clusterOrder.type, null),
                    chainWalk(() => sortPathConfiguration.pickPathSetting.additionalFields.clusterOrder.customSequence, []).join(", "),
                ],
                aisleSequence: [
                    chainWalk(() => sortPathConfiguration.pickPathSetting.additionalFields.aisleOrder.type, null),
                    chainWalk(() => sortPathConfiguration.pickPathSetting.additionalFields.aisleOrder.customSequence, []).join(", ")
                ],
                sortLocationSequence: [
                    chainWalk(() => sortPathConfiguration.pickPathSetting.additionalFields.sortLocationOrder.type, null),
                    chainWalk(() => sortPathConfiguration.pickPathSetting.additionalFields.sortLocationOrder.customSequence, []).join(", ")
                ]
            },
            parameters: {
                columnLengthForZebraPath: chainWalk(() => sortPathConfiguration.pickPathSetting.additionalFields.columnLengthForZebraPath, null)
            }
        },
    };
};

const extractCusterParameterMapFromSortPathSettings = (clusterParameterValuesForNode) => {
    const clusterParameterValues = chainWalk(() => clusterParameterValuesForNode, []);
    let transformedValues = [];
    if (Array.isArray(clusterParameterValues) && clusterParameterValues.length) {
        clusterParameterValues.forEach(parameterValuePerCluster => {
            transformedValues = addToObject(transformedValues, parameterValuePerCluster.clusterName,
                parameterValuePerCluster.clusterParameterValue)
        });
    }
    return transformedValues;
};

const buildClusterParameterModelForSortPathSettingswithDefaultValue = (clusterParameterValues, clusterResources = []) => {
    let transformedValues = [];
    const DEFAULT_CLUSTER_VALUE = "1000000";

    if (Array.isArray(clusterResources) && clusterResources.length) {
        clusterResources.forEach(cluster => {
            transformedValues.push({
                clusterName: cluster.label,
                clusterParameterValue: DEFAULT_CLUSTER_VALUE
            });
        });
    }

    if (Object.entries(clusterParameterValues).length) {
        Object.entries(clusterParameterValues).forEach(entry => {
            const [clusterName, value] = entry;
            const existingIndex = transformedValues.findIndex(item => item.clusterName === clusterName);
            if (existingIndex >= 0) {
                transformedValues[existingIndex].clusterParameterValue = value || DEFAULT_CLUSTER_VALUE;
            }
        });
    }
    return transformedValues;
};

const buildCusterParameterModelForSortPathSettings = (clusterParameterValues) => {
    let transformedValues = [];
    if (Object.entries(clusterParameterValues).length) {
        Object.entries(clusterParameterValues).forEach(entry => {
            const [clusterName, value] = entry;
            transformedValues.push({clusterName: clusterName, clusterParameterValue: value})
        });
    }
    return transformedValues;
};

const isSTAGEnabled  = () => {
    return FeatureConfigUtils.getIsStagFeatureEnabledStation();
}

const getScienceModelOptimizationParameter = (scienceModelOptimizationParameter, key) => {
    const defaultValue = (key !== "balancingSortZonesInClusters");
    if (scienceModelOptimizationParameter && Object.keys(scienceModelOptimizationParameter).length !== 0) {
        return chainWalk(() => scienceModelOptimizationParameter[key], defaultValue);
    }
    return defaultValue;
}

